<template>
  <div
    class="relative flex flex-col w-full h-full text-main-700 dark:text-gray-300 bg-white dark:bg-gray-800 shadow-xl rounded-lg bg-clip-border transition-colors duration-300"
  >
    <!-- Header -->
    <div class="w-full flex flex-col relative">
      <div
        class="flex flex-col justify-between p-6 pb-0 gap-8 xl:flex-wrap xl:items-start"
      >
        <div>
          <h5
            class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
          >
            Todays Transactions
          </h5>
          <p
            class="block text-base font-normal leading-6 text-gray-600 dark:text-gray-400 mt-2 opacity-90 antialiased"
          >
            These are details about todays transactions both offline and online.
          </p>
        </div>
      </div>

      <!-- Table -->

      <template v-if="loading">
        <div
          class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
        >
          <LoadingComponent /></div
      ></template>
      <div v-else class="p-6 overflow-x-auto">
        <table class="w-full text-left table-auto min-w-max">
          <!-- Table Header -->
          <thead class="bg-main-50 dark:bg-gray-700 rounded-lg">
            <tr>
              <th class="p-4">
                <p
                  class="block text-sm antialiased font-medium leading-none text-main-900 dark:text-gray-200"
                >
                  Order ID
                </p>
              </th>
              <th class="p-4">
                <p
                  class="block text-sm antialiased font-medium leading-none text-main-900 dark:text-gray-200"
                >
                  Amount
                </p>
              </th>
              <th class="p-4">
                <p
                  class="block text-sm antialiased font-medium leading-none text-main-900 dark:text-gray-200"
                >
                  Status
                </p>
              </th>
              <th class="p-4">
                <p
                  class="block text-sm antialiased font-medium leading-none text-main-900 dark:text-gray-200"
                >
                  Shipping
                </p>
              </th>
            </tr>
          </thead>
          <!-- Table Body -->
          <tbody>
            <!-- Orders List -->
            <tr
              v-for="order in orders"
              :key="order.id"
              class="hover:bg-blue-gray-50/10 dark:hover:bg-gray-700/10 cursor-pointer transition-colors duration-200"
            >
              <td class="p-4 border-b border-main-50 dark:border-gray-700">
                <router-link :to="`/order/${order.id}`">
                  <div class="flex items-center justify-start gap-3">
                    <p
                      class="block text-sm antialiased font-semibold leading-normal text-start text-main-900 dark:text-gray-200 hover:underline"
                    >
                      {{ order.orderId }}
                    </p>
                  </div>
                </router-link>
              </td>
              <td class="p-4 border-b border-main-50 dark:border-gray-700">
                <p
                  class="block text-sm antialiased font-medium leading-normal text-main-900 dark:text-gray-200"
                >
                  {{ getCurrencySymbol(storeCurrencyId)
                  }}{{ parseFloat(order.amount).toLocaleString() }}
                </p>
              </td>
              <td class="p-4 border-b border-main-50 dark:border-gray-700">
                <div class="w-max">
                  <div
                    class="relative grid items-center px-3 py-1.5 text-xs font-bold uppercase rounded-full select-none whitespace-nowrap"
                    :class="
                      order.status === 1
                        ? 'text-green-800 bg-green-100/80 dark:bg-green-900/20 dark:text-green-300'
                        : 'text-red-800 bg-red-100/80 dark:bg-red-900/20 dark:text-red-300'
                    "
                  >
                    <span>{{ order.status === 1 ? "Paid" : "Unpaid" }}</span>
                  </div>
                </div>
              </td>
              <td class="p-4 border-b border-main-50 dark:border-gray-700">
                <div class="w-max">
                  <div
                    class="relative grid items-center px-3 py-1.5 text-xs font-bold uppercase rounded-full select-none whitespace-nowrap"
                    :class="statusClass(order.shippingStatus)"
                  >
                    <span>{{ currentStatus(order.shippingStatus) }}</span>
                  </div>
                </div>
              </td>
            </tr>

            <!-- No Orders Empty State -->
            <tr v-if="!loading && orders.length === 0">
              <td colspan="6" class="px-4 py-8 sm:px-6">
                <div class="text-center">
                  <div class="mx-auto flex justify-center">
                    <div
                      class="p-4 rounded-full bg-gray-100 dark:bg-gray-700/50"
                    >
                      <svg
                        class="w-10 h-10 text-gray-400 dark:text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                        />
                      </svg>
                    </div>
                  </div>
                  <h3
                    class="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100"
                  >
                    No sales recorded today yet
                  </h3>
                  <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    When you make sales, they'll appear here automatically.
                  </p>
                  <div class="mt-6">
                    <button
                      @click="fetchOrders"
                      class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-main-600 dark:bg-sub-800 border border-transparent rounded-md shadow-sm hover:bg-main-700 dark:hover:bg-sub-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-500 dark:focus:ring-sub-800"
                    >
                      <i class="mr-2 fa-solid fa-rotate"></i>
                      Refresh orders
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { mapActions } from "vuex";

export default {
  name: "RecentOrdersDashboardView",
  data() {
    return {
      orders: [],
      currencies: [],
      loading: true,
    };
  },
  computed: {
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
  },
  mounted() {
    this.getCurrency();
  },
  methods: {
    ...mapActions(["getTodaysOrders", "fetchCurrency"]),
    statusClass(shippingStatus) {
      switch (shippingStatus) {
        case 0: // Pending
          return "text-gray-800 bg-gray-100/80 dark:bg-gray-700/20 dark:text-gray-300";
        case 1: // Order Placed
          return "text-yellow-800 bg-yellow-100/80 dark:bg-yellow-900/20 dark:text-yellow-300";
        case 2: // Order Packed
          return "text-orange-800 bg-orange-100/80 dark:bg-orange-900/20 dark:text-orange-300";
        case 3: // Order Shipped
          return "text-blue-800 bg-blue-100/80 dark:bg-blue-900/20 dark:text-blue-300";
        case 4: // Order Delivered
          return "text-green-800 bg-green-100/80 dark:bg-green-900/20 dark:text-green-300";
        default:
          return "text-gray-800 bg-gray-100/80 dark:bg-gray-700/20 dark:text-gray-300";
      }
    },
    currentStatus(shippingStatus) {
      switch (shippingStatus) {
        case 0:
          return "Pending";
        case 1:
          return "Order Placed";
        case 2:
          return "Order Packed";
        case 3:
          return "Order Shipped";
        case 4:
          return "Order Delivered";
        default:
          return "Pending";
      }
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async fetchOrders() {
      this.loading = true;
      try {
        const response = await this.getTodaysOrders();
        if (response.success) {
          this.orders = response.orders;
        } else {
          toast.error(response.error || "Failed to fetch orders");
        }
      } catch (error) {
        toast.error("Error fetching orders");
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchOrders();
  },
};
</script>
